import React, { useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  StreamingType,
  GiftItemData,
  CommentFilterType,
  streamingActions,
} from "modules/streaming";
import { Store } from "../../store";
import { EventInfo } from "../../utility";
import StreamingCommentComponent from "components/streaming/StreamingComment";
import { Timestamp } from "@google-cloud/firestore";
import { firestoreActions } from "modules/firestore";
import {
  updateFirestoreDoc,
  deleteFirestoreDoc,
  setFirestoreDocument,
} from "../../utility/firebase";
import { isFeatureEnabled } from "../../utility/isFeatureEnabled";
import { getCommentCollectionPath } from "utility/streaming";
import type { THEOPlayer } from "@spwn/types/external";
import type { UserMessage } from "@spwn/types/firebase/firestore";
import { useIsChatAdmin } from "./useIsChatAdmin";
import firebase from "firebase/app";
import "firebase/firestore";

type Props = {
  eventStart: Timestamp;
  streamingType: StreamingType;
  player: THEOPlayer;
  eventInfo: EventInfo;
  giftItemMap: { [key: string]: GiftItemData };
  currentVideoId: string; // !!! don't allow null !!!
  commentFilterState: CommentFilterType;
  THEOPlayerElementHeight?: number;
};

const StreamingComment: React.FC<Props> = (props) => {
  const user = useSelector((state: Store) => state.auth.user);
  const displayComments = useSelector(
    (state: Store) => state.streaming.displayComments
  );
  const eventGoodsMap = useSelector(
    (state: Store) => state.ticket.eventGoodsMap
  );
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const commentShardId = useSelector(
    (state: Store) => state.streaming.commentShardId
  );

  const dispatch = useDispatch();

  const fetchDisplayComments = () => {
    if (props.streamingType === "Live") {
      if (
        props.eventInfo._id &&
        isFeatureEnabled({
          featureName: "polling",
          eventId: props.eventInfo._id,
        })
      ) {
        dispatch(
          streamingActions.startCommentPolling.started({
            eventId: props.eventInfo._id,
            videoId: props.currentVideoId,
          })
        );
      } else if (isFeatureEnabled({ featureName: "newComment" })) {
        dispatch(
          streamingActions.newFetchDisplayComments.started({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            eventId: props.eventInfo._id!,
            videoId: props.currentVideoId,
          })
        );
      } else {
        dispatch(
          streamingActions.fetchDisplayComments.started({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            eventId: props.eventInfo._id!,
            videoId: props.currentVideoId,
          })
        );
      }
      dispatch(
        streamingActions.fetchBANComments.started({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          eventId: props.eventInfo._id!,
          videoId: props.currentVideoId,
        })
      );
      dispatch(
        streamingActions.fetchBanUserIds.started({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          eventId: props.eventInfo._id!,
          videoId: props.currentVideoId,
        })
      );
    } else {
      handleSeekEvents();
    }
  };

  const handleSeekEvents = () => {
    console.log("handleSeekEvents", props.currentVideoId);
    dispatch(
      streamingActions.handleSeekEvents.started({
        // @ts-expect-error TS2322
        eventId: props.eventInfo._id,
        videoId: props.currentVideoId,
        player: props.player,
        eventStart: props.eventStart,
      })
    );
  };

  const isChatAdmin = useIsChatAdmin(props.eventInfo._id);

  const banComment = useCallback(
    async (commentId: string) => {
      const msgCollectionRef = getCommentCollectionPath(
        // @ts-expect-error TS2345
        props.eventInfo._id,
        props.currentVideoId,
        props.eventInfo?.streamingInfo?.optionSettings
          ?.isCommentShardingEnabled,
        commentShardId.value
      );
      const msgRef = `${msgCollectionRef}/${commentId}`;
      await updateFirestoreDoc(msgRef, { isBan: true });
    },
    [
      commentShardId,
      props.currentVideoId,
      props.eventInfo._id,
      props.eventInfo?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
    ]
  );

  /**
   * 管理者だけがBan処理する
   */
  const banCommentAdminOnly = useCallback(
    async (commentId: string) => {
      if (!isChatAdmin) {
        return;
      }
      await banComment(commentId);
    },
    [banComment, isChatAdmin]
  );

  /**
   * コメントを物理削除する
   *
   * 削除の際にBAN処理を呼んでから削除している理由：
   * - onSnapshotでは削除されたことを検知はしない
   * - そのため、一度banすることで変更検知を走らせて、コメントを更新している
   * - その後時間をおいて物理削除する
   */
  const deleteComment = useCallback(
    async (commentId: string) => {
      await banComment(commentId);
      // delete from display
      dispatch(streamingActions.deleteDisplayComment(commentId));
      // delay deletion to detect baned in fetchBANComments
      window.setTimeout(() => {
        const msgCollectionRef = getCommentCollectionPath(
          // @ts-expect-error TS2345
          props.eventInfo._id,
          props.currentVideoId,
          props.eventInfo?.streamingInfo?.optionSettings
            ?.isCommentShardingEnabled,
          commentShardId.value
        );
        const msgRef = `${msgCollectionRef}/${commentId}`;
        // console.info(msgRef)
        deleteFirestoreDoc(msgRef);
      }, 5000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      banComment,
      commentShardId,
      props.currentVideoId,
      props.eventInfo._id,
      props.eventInfo?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
    ]
  );

  // switch watching video
  useEffect(() => {
    console.log("didMount StreamingComment");
    if (commentShardId.type === "init") return;

    switch (props.commentFilterState) {
      case "all":
        fetchDisplayComments();
        break;
      case "none":
        dispatch(streamingActions.stopCommentPolling());
        dispatch(
          firestoreActions.closeChannel({ channel: "streamingComments" })
        );
        dispatch(
          firestoreActions.closeChannel({ channel: "streamingBANComments" })
        );
        break;
      default:
        fetchDisplayComments();
    }
    if (props.streamingType === "VOD" && !!props.player) {
      props.player.addEventListener("seeked", handleSeekEvents);
    }
    // unMount
    return () => {
      console.log("unMount StreamingComment");
      dispatch(streamingActions.stopCommentPolling());
      dispatch(firestoreActions.closeChannel({ channel: "streamingComments" }));
      dispatch(
        firestoreActions.closeChannel({ channel: "streamingBANComments" })
      );
      if (props.streamingType === "VOD" && !!props.player) {
        props.player.removeEventListener("seeked", handleSeekEvents);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    /**
     * // FIXME 現状useEffectを正しく使えていない。useCallbackなど使う。
     * fetchDisplayComments で currentVideoId を利用しているため依存関係に入れてる
     */
    props.currentVideoId,
    props.streamingType,
    props.commentFilterState,
    commentShardId,
  ]);

  // VOD時にコメントを再現する用の処理
  const previousTime = useRef(0);
  useEffect(() => {
    // only use in VOD
    if (props.streamingType === "Live") {
      return;
    }

    const handler = (e: { currentTime: number }) => {
      const currentTime = Math.floor(e.currentTime);
      if (
        !props.eventStart ||
        currentTime === previousTime.current ||
        props.player.paused
      ) {
        return;
      }

      previousTime.current = currentTime;

      dispatch(
        streamingActions.fetchDisplayCommentsByTimeUpdate.started({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          eventId: props.eventInfo._id!,
          videoId: props.currentVideoId,
          currentTime,
          eventStart: props.eventStart,
        })
      );
    };

    props.player.addEventListener("timeupdate", handler);
    return () => props.player.removeEventListener("timeupdate", handler);
  }, [
    props.streamingType,
    props.player,
    props.eventInfo._id,
    props.currentVideoId,
    props.eventStart,
    dispatch,
  ]);

  // filter comment by filter type
  const filteredDisplayComments = useMemo<UserMessage[]>(() => {
    switch (props.commentFilterState) {
      case "all":
        return displayComments;
      case "none":
        return [];
      default:
        return displayComments;
    }
  }, [displayComments, props.commentFilterState]);

  const banUser = useCallback(
    async (banTargetComment: Pick<UserMessage, "_id" | "uid">) => {
      if (!banTargetComment || !props.eventInfo._id) return;

      // ユーザーブロック
      const msgCollectionParentRef = getCommentCollectionPath(
        props.eventInfo._id,
        props.currentVideoId,
        props.eventInfo?.streamingInfo?.optionSettings
          ?.isCommentShardingEnabled,
        commentShardId.value
      ).slice(0, -5); // remove '/msgs'
      await setFirestoreDocument(msgCollectionParentRef, {
        banUserIds: firebase.firestore.FieldValue.arrayUnion(
          banTargetComment.uid
        ),
      });
    },
    [
      commentShardId,
      props.currentVideoId,
      props.eventInfo._id,
      props.eventInfo?.streamingInfo?.optionSettings?.isCommentShardingEnabled,
    ]
  );

  return (
    <StreamingCommentComponent
      player={props.player}
      eventStart={props.eventStart}
      streamingType={props.streamingType}
      eventInfo={props.eventInfo}
      eventGoodsMap={eventGoodsMap}
      giftItemMap={props.giftItemMap}
      currentVideoId={props.currentVideoId}
      displayComments={filteredDisplayComments}
      user={user}
      isDarkModeEnabled={isDarkModeEnabled}
      THEOPlayerElementHeight={props.THEOPlayerElementHeight}
      commentFilterState={props.commentFilterState}
      banUser={banUser}
      banComment={banCommentAdminOnly}
      deleteComment={deleteComment}
    />
  );
};

export default StreamingComment;
